import React, { useEffect, useState } from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import { dashboardService, type MesDaEvolucao } from '../../services/api';
import { A_PROPRIA_LISTA_DIZ_O_VAZIO, EstadoDaLista, useEstadoDaCarga } from '../../design';

/**
 * A EVOLUÇÃO DO FATURAMENTO no Dashboard (decisões G1 e M1 do Heitor, 23/10/2026), no lugar do "Pipeline por Estágio".
 *
 * Duas barras por mês, a partir de fev/2026 (jan foi a transição do Superlógica): o FATURADO por competência e o RECEBIDO
 * pelo mês do pagamento. A série vem pronta do backend (`GET /dashboard/evolucao`).
 *  - O mês corrente vem com `parcial: true` e é desenhado como parcial (barra esmaecida e "*"): sem isso, outubro pela
 *    metade parecia uma queda de 22% no recebido.
 *  - MRR: o banco não tem histórico. A linha tracejada é o MRR de HOJE, como referência (M1); quando o backend tiver a foto
 *    do fim de cada mês (`mrr` deixa de ser null), a linha mensal aparece por cima, sem mudar esta peça.
 */
const COR = { faturado: '#64748b', recebido: '#34d399', mrr: '#a78bfa' };
const MES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const fmt = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** "2026-10" e parcial → "out*". */
export function rotuloDoMes(m: Pick<MesDaEvolucao, 'mes' | 'parcial'>): string {
  const [, mm] = m.mes.split('-');
  return `${MES[Number(mm) - 1] || m.mes}${m.parcial ? '*' : ''}`;
}

/** A linha do MRR mensal só aparece quando há ao menos uma foto (até lá, todos vêm null). */
export const temFotoDoMrr = (meses: MesDaEvolucao[]): boolean => meses.some((m) => m.mrr !== null && m.mrr !== undefined);

const Dica = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const m: MesDaEvolucao = payload[0].payload;
  return (
    <div className='bg-central-cartao border border-central-borda rounded-lg p-3 shadow-xl text-sm'>
      <p className='text-central-texto font-medium'>{rotuloDoMes({ ...m, parcial: false })} {m.mes.split('-')[0]}{m.parcial ? ' (parcial)' : ''}</p>
      <p className='text-central-texto-fraco'>Faturado: <span className='text-central-texto'>R$ {fmt(m.faturado)}</span> · {m.faturas} fat.</p>
      <p className='text-central-texto-fraco'>Recebido: <span className='text-central-texto'>R$ {fmt(m.recebido)}</span> · {m.pagamentos} pag.</p>
      {m.mrr !== null && m.mrr !== undefined && <p className='text-central-texto-fraco'>MRR no fim do mês: <span className='text-central-texto'>R$ {fmt(m.mrr)}</span></p>}
    </div>
  );
};

export default function EvolucaoDoFaturamento({ mrrDeHoje }: { mrrDeHoje: number | null }) {
  const { rodar, ...carga } = useEstadoDaCarga();
  const [meses, setMeses] = useState<MesDaEvolucao[] | null>(null);

  const ler = () => rodar(async () => {
    const r = await dashboardService.evolucao();
    setMeses(Array.isArray(r?.meses) ? r.meses : []);
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { ler(); }, []);

  const dados = (meses || []).map((m) => ({ ...m, rotulo: rotuloDoMes(m) }));
  const comFoto = temFotoDoMrr(meses || []);
  const parcial = (meses || []).find((m) => m.parcial);

  return (
    <div className='bg-central-cartao rounded-central border border-central-borda overflow-hidden' data-cartao='evolucao'>
      <div className='p-4 border-b border-central-borda'>
        <h3 className='text-central-texto font-semibold'>Evolução do faturamento</h3>
        <p className='text-central-texto-fraco text-sm'>Faturado por competência e recebido no mês, desde fevereiro de 2026</p>
      </div>
      <div className='p-4'>
        <EstadoDaLista carga={carga} temDados={!!meses} oQue='a evolução do faturamento' onTentarDeNovo={ler} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>
          {dados.length === 0 ? (
            <p className='text-central-texto-fraco text-sm' data-evolucao='vazio'>Ainda não há mês fechado para mostrar.</p>
          ) : (
            <>
              <div style={{ width: '100%', height: 260 }} data-evolucao='grafico'>
                <ResponsiveContainer>
                  <ComposedChart data={dados} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray='3 3' stroke='#334155' vertical={false} />
                    <XAxis dataKey='rotulo' tick={{ fill: '#94a3b8', fontSize: 12 }} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={(v: number) => `${Math.round(v / 1000)} mil`} width={56} />
                    <Tooltip content={<Dica />} cursor={{ fill: 'rgba(148,163,184,0.08)' }} />
                    <Bar dataKey='faturado' name='Faturado' fill={COR.faturado} radius={[3, 3, 0, 0]}>
                      {dados.map((m) => <Cell key={`f-${m.mes}`} fillOpacity={m.parcial ? 0.5 : 1} />)}
                    </Bar>
                    <Bar dataKey='recebido' name='Recebido' fill={COR.recebido} radius={[3, 3, 0, 0]}>
                      {dados.map((m) => <Cell key={`r-${m.mes}`} fillOpacity={m.parcial ? 0.5 : 1} />)}
                    </Bar>
                    {comFoto && <Line type='monotone' dataKey='mrr' name='MRR' stroke={COR.mrr} strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />}
                    {mrrDeHoje !== null && mrrDeHoje > 0 && (
                      <ReferenceLine y={mrrDeHoje} stroke={COR.mrr} strokeDasharray='6 4' ifOverflow='extendDomain'
                        label={{ value: `MRR de hoje: R$ ${fmt(mrrDeHoje)}`, position: 'insideTopRight', fill: COR.mrr, fontSize: 11 }} />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div className='flex flex-wrap gap-4 mt-2 text-xs text-central-texto-fraco' data-evolucao='legenda'>
                <span><span className='inline-block w-2.5 h-2.5 rounded-sm mr-1.5 align-middle' style={{ background: COR.faturado }} />Faturado (competência)</span>
                <span><span className='inline-block w-2.5 h-2.5 rounded-sm mr-1.5 align-middle' style={{ background: COR.recebido }} />Recebido (mês do pagamento)</span>
                <span><span className='inline-block w-3.5 mr-1.5 align-middle border-t-2 border-dashed' style={{ borderColor: COR.mrr }} />MRR de hoje{comFoto ? ' e no fim de cada mês' : ''}</span>
                {parcial && <span data-evolucao='parcial'>* {rotuloDoMes({ ...parcial, parcial: false })} ainda não fechou</span>}
              </div>
            </>
          )}
        </EstadoDaLista>
      </div>
    </div>
  );
}
